import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Buffer } from 'buffer';
import { storefrontApi } from '../api/Api';

import ProductItem from './utils/ProductItem';

class Products extends Component {
    constructor(props) {
        super(props);

        this.state = {
            productsList: [],
            category: {},
            filters: {},
            sortOptions: [],
            pagination: {},
            serviceCheck: false,
            productsLoading: false,
            q: '',
            minPrice: '',
            maxPrice: '',
            inStock: false,
            sort: 'latest',
            perPage: 20,
            subCategoryIds: [],
            filtersLoadedFor: '',
        };

        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleFilterSubmit = this.handleFilterSubmit.bind(this);
        this.handleSubcategoryToggle = this.handleSubcategoryToggle.bind(this);
        this.clearFilters = this.clearFilters.bind(this);
    }

    componentDidMount() {
        this.syncStateFromQuery();
        this.loadCategoryPage(true);
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.match.params.id !== prevProps.match.params.id ||
            this.props.match.params.name !== prevProps.match.params.name ||
            this.props.location.search !== prevProps.location.search
        ) {
            this.syncStateFromQuery();
            this.loadCategoryPage(
                this.props.match.params.id !== prevProps.match.params.id ||
                this.props.match.params.name !== prevProps.match.params.name
            );
        }
    }

    decodeCategoryId(value) {
        try {
            return Buffer.from(value || '', 'base64').toString('ascii');
        } catch (error) {
            return '';
        }
    }

    getCurrentQuery() {
        return new URLSearchParams(this.props.location.search || '');
    }

    syncStateFromQuery() {
        const query = this.getCurrentQuery();
        this.setState({
            q: query.get('q') || '',
            minPrice: query.get('min_price') || '',
            maxPrice: query.get('max_price') || '',
            inStock: ['true', '1'].includes(query.get('in_stock')),
            sort: query.get('sort') || 'latest',
            perPage: Number(query.get('per_page') || 20),
            subCategoryIds: (query.get('sub_category_ids') || query.get('sub_category_id') || '')
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
        });
    }

    buildBaseParams() {
        const query = this.getCurrentQuery();
        const routeId = this.props.match.params.id;
        const routeName = this.props.match.params.name;
        const finalCategoryId = routeId ? this.decodeCategoryId(routeId) : '';
        const params = query.get('scope') === 'category'
            ? { category_slug: routeName }
            : finalCategoryId
            ? { final_category_id: finalCategoryId }
            : { final_category_slug: routeName };
        const countryId = localStorage.getItem('country_id');

        if (countryId) params.country_id = countryId;

        return params;
    }

    buildApiParams(overrides = {}) {
        const query = this.getCurrentQuery();
        const params = {
            ...this.buildBaseParams(),
            page: overrides.page || query.get('page') || 1,
            per_page: overrides.per_page || query.get('per_page') || 20,
            sort: overrides.sort || query.get('sort') || 'latest',
        };
        const q = overrides.q !== undefined ? overrides.q : query.get('q');
        const minPrice = overrides.min_price !== undefined ? overrides.min_price : query.get('min_price');
        const maxPrice = overrides.max_price !== undefined ? overrides.max_price : query.get('max_price');
        const inStock = overrides.in_stock !== undefined ? overrides.in_stock : query.get('in_stock');

        if (q) params.q = q;
        if (minPrice) params.min_price = minPrice;
        if (maxPrice) params.max_price = maxPrice;
        if (inStock === true || inStock === 'true' || inStock === '1') params.in_stock = 1;

        const subCategoryIds = overrides.sub_category_ids !== undefined
            ? overrides.sub_category_ids
            : (query.get('sub_category_ids') || query.get('sub_category_id') || '');
        if (subCategoryIds && query.get('scope') === 'category') {
            params.sub_category_ids = Array.isArray(subCategoryIds) ? subCategoryIds.join(',') : subCategoryIds;
        }

        return params;
    }

    updateQuery(nextValues) {
        const query = this.getCurrentQuery();

        Object.keys(nextValues).forEach((key) => {
            const value = nextValues[key];
            if (value === '' || value === false || value === null || value === undefined) {
                query.delete(key);
            } else {
                query.set(key, value);
            }
        });

        this.props.history.push({
            pathname: this.props.location.pathname,
            search: query.toString() ? `?${query.toString()}` : '',
        });
    }

    normalizeProduct(product, category = {}) {
        const pricing = product.pricing || {};
        const offer = product.offer || pricing.offer || {};
        const media = product.media || {};
        const defaultSku = product.default_sku || {};
        const hasOffer = Boolean(offer.has_offer);
        const mrpPrice = pricing.mrp || defaultSku.mrp || product.mrp || '';
        const activePrice = hasOffer
            ? (pricing.offer_price || offer.offer_price || pricing.selling_price)
            : pricing.selling_price;
        const effectiveDiscountPercent = Number(mrpPrice || 0) > Number(activePrice || 0)
            ? ((Number(mrpPrice) - Number(activePrice)) / Number(mrpPrice)) * 100
            : 0;

        return {
            ...product,
            product_id: product.id,
            variation_id: defaultSku.id || product.id,
            skus: Array.isArray(product.skus) ? product.skus : [],
            product_name: product.name,
            slug: product.slug,
            product_subtitle: product.short_description || product.sku || '',
            display_image: media.primary_image_url || (media.images && media.images[0] && media.images[0].url) || '',
            offer,
            offer_price: activePrice,
            selling_price: mrpPrice,
            mrp: mrpPrice,
            badges: Array.isArray(product.badges) ? product.badges : [],
            discount_percent: effectiveDiscountPercent,
            pricing: {
                ...pricing,
                offer,
                offer_price: activePrice,
                original_price: mrpPrice,
                mrp: mrpPrice,
                discount_percent: effectiveDiscountPercent,
            },
            category_name: category.name,
            in_stock: product.inventory && product.inventory.in_stock,
        };
    }

    async loadCategoryPage(shouldLoadFilters = false) {
        this.setState(shouldLoadFilters ? { serviceCheck: false, productsLoading: true } : { productsLoading: true });

        try {
            let category = this.state.category || {};

            if (shouldLoadFilters) {
                const filtersResponse = await storefrontApi.getProductFilters(this.buildBaseParams());
                const filterData = filtersResponse && filtersResponse.data ? (filtersResponse.data.data || {}) : {};
                category = filterData.category || category;

                this.setState({
                    category,
                    filters: filterData.filters || {},
                    sortOptions: Array.isArray(filterData.sort_options) ? filterData.sort_options : [],
                    filtersLoadedFor: this.props.match.params.id,
                    serviceCheck: true,
                    productsLoading: true,
                });
            }

            const productsResponse = await storefrontApi.getProducts(this.buildApiParams());
            const productData = (productsResponse.data && productsResponse.data.data) || {};

            this.setState({
                productsList: Array.isArray(productData.products) ? productData.products.map((product) => this.normalizeProduct(product, category)) : [],
                category,
                pagination: productData.pagination || {},
                serviceCheck: true,
                productsLoading: false,
            });
        } catch (error) {
            console.error(error);
            this.setState({
                productsList: [],
                pagination: {},
                serviceCheck: true,
                productsLoading: false,
            });
        }
    }

    handleFilterChange(event) {
        const { name, value, type, checked } = event.target;
        const nextValue = type === 'checkbox' ? checked : value;
        this.setState({ [name]: nextValue });

        if (name === 'inStock') {
            this.updateQuery({
                in_stock: checked ? '1' : '',
                page: 1,
            });
        }
    }

    handleFilterSubmit(event) {
        event.preventDefault();
        this.updateQuery({
            q: this.state.q,
            min_price: this.state.minPrice,
            max_price: this.state.maxPrice,
            in_stock: this.state.inStock ? '1' : '',
            page: 1,
        });
    }

    handleSubcategoryToggle(event) {
        const value = event.target.value;
        const subCategoryIds = event.target.checked
            ? [...this.state.subCategoryIds, value]
            : this.state.subCategoryIds.filter((id) => id !== value);

        this.setState({ subCategoryIds });
        this.updateQuery({
            sub_category_ids: subCategoryIds.join(','),
            page: 1,
        });
    }

    clearFilters() {
        this.setState({
            q: '',
            minPrice: '',
            maxPrice: '',
            inStock: false,
            subCategoryIds: [],
        });
        this.updateQuery({
            q: '',
            min_price: '',
            max_price: '',
            in_stock: '',
            sub_category_ids: '',
            page: 1,
        });
    }

    getCategoryBannerImage(category = {}) {
        return category.image_url || '';
    }

    renderBreadcrumb() {
        const categoryName = this.state.category.name || this.props.match.params.name || 'Products';
        const crumbs = [
            { label: 'Home', to: '/' },
            { label: categoryName, to: this.props.location.pathname },
        ];

        return (
            <ul className="ps-products-page__breadcrumbs">
                {crumbs.map((crumb, index) => {
                    const isLast = index === crumbs.length - 1;
                    return (
                        <li className={`ps-products-page__breadcrumb ${isLast ? 'ps-products-page__breadcrumb--active' : ''}`} key={`${crumb.label}-${index}`}>
                            {isLast ? <span>{crumb.label}</span> : <Link to={crumb.to}>{crumb.label}</Link>}
                        </li>
                    );
                })}
            </ul>
        );
    }

    renderFilterOptions(title, options = []) {
        if (!Array.isArray(options) || options.length === 0) return null;

        return (
            <div className={`ps-products-filter__group ${title === 'Availability' ? 'ps-products-filter__group--availability' : ''}`}>
                <h4>{title}</h4>
                <div className="ps-products-filter__options">
                    {options.map((option, index) => (
                        <span className="ps-products-filter__chip" key={`${title}-${option.value || option.name || index}`}>
                            {option.label || option.name || option.value}
                            {option.count !== undefined ? <em>{option.count}</em> : null}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    renderFilters() {
        const priceRange = (this.state.filters && this.state.filters.price_range) || {};
        const availability = (this.state.filters && this.state.filters.availability) || [];
        const variants = (this.state.filters && this.state.filters.variants) || [];
        const specifications = (this.state.filters && this.state.filters.specifications) || [];
        const subcategories = (this.state.filters && this.state.filters.subcategories) || [];

        return (
            <aside className="ps-products-filter">
                <div className="ps-products-filter__head">
                    <div>
                        <span className="ps-products-filter__eyebrow">Refine results</span>
                        <h3>Filters</h3>
                    </div>
                    <button type="button" onClick={this.clearFilters}>Clear all</button>
                </div>

                {subcategories.length > 0 ? (
                    <div className="ps-products-filter__group ps-products-filter__group--first">
                        <h4>Categories</h4>
                        <div className="ps-products-filter__checkList">
                            {subcategories.map((option) => (
                                <label className="ps-products-filter__check ps-products-filter__check--option" key={option.value}>
                                    <input
                                        type="checkbox"
                                        value={option.value}
                                        checked={this.state.subCategoryIds.includes(String(option.value))}
                                        onChange={this.handleSubcategoryToggle}
                                    />
                                    <span>{option.label}</span>
                                    {option.count !== undefined ? <em>{option.count}</em> : null}
                                </label>
                            ))}
                        </div>
                    </div>
                ) : null}

                <form onSubmit={this.handleFilterSubmit}>
                    <label className="ps-products-filter__field">
                        <span>Search</span>
                        <input name="q" value={this.state.q} onChange={this.handleFilterChange} placeholder="Search products" />
                    </label>

                    <div className="ps-products-filter__range">
                        <label className="ps-products-filter__field">
                            <span>Min price</span>
                            <input name="minPrice" type="number" min="1" value={this.state.minPrice} onChange={this.handleFilterChange} placeholder={priceRange.min || 'Min'} />
                        </label>
                        <label className="ps-products-filter__field">
                            <span>Max price</span>
                            <input name="maxPrice" type="number" min="1" value={this.state.maxPrice} onChange={this.handleFilterChange} placeholder={priceRange.max || 'Max'} />
                        </label>
                    </div>

                    <label className="ps-products-filter__check">
                        <input name="inStock" type="checkbox" checked={this.state.inStock} onChange={this.handleFilterChange} />
                        <span>In stock only</span>
                    </label>

                    <button className="ps-products-filter__apply" type="submit">Apply filters</button>
                </form>

                {this.renderFilterOptions('Availability', availability)}
                {variants.map((variant) => this.renderFilterOptions(variant.name, variant.options))}
                {specifications.map((group) => (
                    <div className="ps-products-filter__group" key={group.id || group.name}>
                        <h4>{group.name}</h4>
                        {(group.items || []).map((item) => this.renderFilterOptions(item.name, item.options))}
                    </div>
                ))}
            </aside>
        );
    }

    renderPagination() {
        const pagination = this.state.pagination || {};
        const current = Number(pagination.current_page || 1);
        const last = Number(pagination.last_page || 1);
        if (last <= 1) return null;

        const pages = Array.from({ length: last }).map((_, index) => index + 1);

        return (
            <div className="ps-products-pagination">
                <button type="button" disabled={current <= 1} onClick={() => this.updateQuery({ page: current - 1 })}>
                    <i className="fa fa-angle-left"></i>
                </button>
                {pages.map((page) => (
                    <button
                        type="button"
                        className={page === current ? 'ps-products-pagination__page--active' : ''}
                        key={page}
                        onClick={() => this.updateQuery({ page })}
                    >
                        {page}
                    </button>
                ))}
                <button type="button" disabled={current >= last} onClick={() => this.updateQuery({ page: current + 1 })}>
                    <i className="fa fa-angle-right"></i>
                </button>
            </div>
        );
    }

    renderCategoryLoader() {
        return (
            <div className="ps-products-page ps-products-page--loading" aria-label="Loading category products">
                <section className="ps-products-page__hero">
                    <div className="container">
                        <div className="ps-category-skeleton__breadcrumbs">
                            <span></span><span></span>
                        </div>
                    </div>
                </section>

                <section className="ps-products-page__catalog">
                    <div className="container">
                        <div className="ps-products-page__catalog-head ps-category-skeleton__head">
                            <span className="ps-category-skeleton__title"></span>
                            <div className="ps-category-skeleton__toolbar"><span></span><span></span></div>
                        </div>

                        <div className="ps-products-page__layout">
                            <aside className="ps-products-filter ps-category-skeleton__filter">
                                <span className="ps-category-skeleton__filterHead"></span>
                                <span className="ps-category-skeleton__line ps-category-skeleton__line--wide"></span>
                                <span className="ps-category-skeleton__line"></span>
                                <span className="ps-category-skeleton__line"></span>
                                <span className="ps-category-skeleton__line"></span>
                                <span className="ps-category-skeleton__line"></span>
                            </aside>
                            <div className="ps-category-skeleton__grid">
                                {this.renderCategorySkeletonProducts()}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    renderCategorySkeletonProducts() {
        return Array.from({ length: 6 }).map((_, index) => (
                                    <div className="ps-category-skeleton__product" key={index}>
                                        <span className="ps-category-skeleton__image"></span>
                                        <span className="ps-category-skeleton__productLine"></span>
                                        <span className="ps-category-skeleton__productLine ps-category-skeleton__productLine--short"></span>
                                    </div>
        ));
    }

    render() {
        if (this.state.serviceCheck === false) {
            return this.renderCategoryLoader();
        }

        const { productsList, category } = this.state;
        const bannerImage = this.getCategoryBannerImage(category);

        return (
            <div className={`ps-products-page ${this.state.productsLoading ? 'ps-products-page--loading' : ''}`}>
                <section className="ps-products-page__hero">
                    <div className="container">
                        {this.renderBreadcrumb()}
                        <div className="ps-products-page__hero-grid">
                            <div className="ps-products-page__hero-visual" aria-hidden="true">
                                <div className="ps-products-page__hero-visualInner">
                                    {bannerImage ? <img src={bannerImage} alt="" className="ps-products-page__hero-image" /> : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="ps-products-page__catalog">
                    <div className="container">
                        <div className="ps-products-page__catalog-head">
                            <div>
                                <h2 className="ps-products-page__catalog-title">
                                    <span className="ps-products-page__catalog-eyebrow">Products</span>
                                    <span className="ps-products-page__catalog-titleName">{category.name || 'Category products'}</span>
                                </h2>
                            </div>
                            <div className="ps-products-page__toolbar">
                                <select value={this.state.sort} onChange={(event) => this.updateQuery({ sort: event.target.value, page: 1 })}>
                                    {(this.state.sortOptions.length ? this.state.sortOptions : [{ value: 'latest', label: 'Latest' }]).map((option) => (
                                        <option value={option.value} key={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <select value={String(this.state.perPage)} onChange={(event) => this.updateQuery({ per_page: event.target.value, page: 1 })}>
                                    {[12, 20, 32, 48].map((count) => <option value={count} key={count}>{count} / page</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="ps-products-page__layout">
                            {this.renderFilters()}
                            <div className={`ps-products-page__results ${this.state.productsLoading ? 'ps-products-page__results--loading' : ''}`}>
                                {this.state.productsLoading ? (
                                    <div className="ps-products-page__resultsLoader">
                                        <div className="ps-category-skeleton__grid ps-category-skeleton__grid--overlay">
                                            {this.renderCategorySkeletonProducts()}
                                        </div>
                                    </div>
                                ) : null}
                                {productsList.length > 0 ? (
                                    <>
                                        <div className="row g-4 ps-products-page__grid">
                                            {productsList.map((productInfo, index) => (
                                                <div className="col-12 col-sm-6 col-lg-4" key={productInfo.variation_id || productInfo.product_id || index}>
                                                    <ProductItem product_details={productInfo} showPriceOffer />
                                                </div>
                                            ))}
                                        </div>
                                        {this.renderPagination()}
                                    </>
                                ) : this.state.productsLoading ? null : (
                                    <div className="ps-products-page__empty">
                                        <div className="ps-products-page__emptyCard">
                                            <span className="ps-products-page__emptyEyebrow">No results</span>
                                            <h3>We could not find matching products.</h3>
                                            <p>Try changing the filters or browse another category from the homepage.</p>
                                            <button type="button" className="ps-products-page__emptyButton" onClick={this.clearFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default Products;
